import { useChatMessages, useChatStatus } from '@ai-sdk-tools/store';
import { motion } from 'framer-motion';
import { ArrowDownIcon, Loader2 } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentType } from 'react';

import { InlineError } from './inline-error';
import { PreviewMessage } from './preview-message';
import type { PreviewMessageProps } from './preview-message';
import { useMessages } from '../../../hooks/use-messages';
import type { MessageReaction, UIMessageWithMetadata } from '../../../lib/types';
import { Greeting } from '../../greeting';
import { ThinkingMessage } from '../../ui/thinking-message';
import { useChatOverlayContextSafe } from '../contexts/chat-overlay-context';
import { useDurablePreviews } from '../contexts/durable-preview-context';
import type { DataPartRenderer, ToolOutputRenderer } from '../lib/default-renderers';
import { getRenderableDurablePreviewMessage, hasVisibleDurablePreviewText } from '../lib/durable-preview';
import { normalizeChatUuid } from '../lib/notification-helpers';
import { observeNovaTurnText, settleNovaTurnTelemetry } from '../lib/nova-turn-telemetry';
import { buildPriorSourcesByMessageId, type SourcesMap } from '../lib/sources';
import { isDurableActiveStreamAlreadyPersisted, isDurableActiveStreamBaseHydrated } from '../lib/transport';

// Re-export the default renderer and its composable contract for external use.
export { PreviewMessage, type PreviewMessageProps };

interface MessagesProps {
  chatUuid?: string;
  className?: string;
  isLoading?: boolean;
  /**
   * Collapses regenerated answers behind version controls by default. Timeline
   * mode renders every persisted answer variant in order for audit surfaces.
   */
  variantDisplayMode?: 'collapsed' | 'timeline';
  welcomeMessage?: string;
  welcomeIcon?: React.ReactNode;
  /**
   * Object mapping tool names to their renderer functions.
   * Keys are tool names (e.g., 'processFiles', 'image_generation').
   * Values are renderer functions that take the part and messageUuid.
   */
  toolRenderers?: Record<string, ToolOutputRenderer>;
  /**
   * Object mapping data part types to their renderer functions.
   * Keys are data part types (e.g., 'data-coverageGapAnalysis').
   * Values are renderer functions that take the part and message.
   */
  dataPartRenderers?: Record<string, DataPartRenderer>;
  /**
   * Custom message renderer component.
   * If provided, this component will be used instead of the default PreviewMessage.
   * Must accept the same props as PreviewMessage.
   */
  messageRenderer?: ComponentType<PreviewMessageProps>;
  /**
   * Controls whether inline citations and sources are rendered.
   * Passed through to PreviewMessage component.
   */
  enableSourcesRendering?: boolean;
  /**
   * Shows each answer's chain of thought (reasoning, tool steps, progress).
   * Without it, a thinking placeholder shows while the answer loads. Defaults to true.
   */
  showChainOfThought?: boolean;
}

/**
 * Messages component that displays chat messages using @ai-sdk-tools/store.
 * No prop drilling needed - messages and status are accessed via context.
 * Handles its own loading state overlay.
 * Reads progressMessage from ChatRoot context.
 * Accepts toolRenderers and dataPartRenderers as props for composability.
 *
 * @example Using a custom message renderer:
 * ```tsx
 * <Messages messageRenderer={MyCustomMessageRenderer} />
 * ```
 */
export function Messages({
  chatUuid,
  className,
  isLoading,
  variantDisplayMode = 'collapsed',
  toolRenderers,
  dataPartRenderers,
  messageRenderer: MessageRenderer = PreviewMessage,
  enableSourcesRendering,
  showChainOfThought = true,
  welcomeMessage,
  welcomeIcon,
}: MessagesProps) {
  const messages = useChatMessages<UIMessageWithMetadata>();
  const status = useChatStatus();
  const overlayContext = useChatOverlayContextSafe();
  const onReaction = overlayContext?.callbacks?.onReaction;
  const durablePreviews = useDurablePreviews();
  const pendingRegenerationMessage = overlayContext?.pendingRegenerationMessage;
  const messageNavigationTarget = overlayContext?.messageNavigationTarget;
  const clearMessageNavigationTarget = overlayContext?.clearMessageNavigationTarget;
  const messageElementsRef = useRef(new Map<string, HTMLDivElement>());

  const [reactions, setReactions] = useState<Record<string, MessageReaction | null>>({});

  const {
    containerRef: messagesContainerRef,
    endRef: messagesEndRef,
    isAtBottom,
    scrollToBottom,
    scrollToElement,
    onViewportEnter,
    onViewportLeave,
  } = useMessages({
    chatUuid,
    status,
  });

  const messagesWithPendingRegeneration = useMemo(
    () => insertPendingRegenerationMessage(messages, pendingRegenerationMessage),
    [messages, pendingRegenerationMessage],
  );
  const variantGroups = useMemo(
    () => groupMessageVariants(messagesWithPendingRegeneration),
    [messagesWithPendingRegeneration],
  );
  const [variantSelectionState, setVariantSelectionState] = useState<{
    chatUuid?: string;
    selections: Record<string, { messageId: string; variantCount: number }>;
  }>({ chatUuid, selections: {} });
  const pendingRegenerationRootId = pendingRegenerationMessage
    ? (pendingRegenerationMessage.metadata?.regeneration?.rootMessageUuid ?? pendingRegenerationMessage.id)
    : undefined;
  const displayedVariantGroups = useMemo(() => {
    if (variantDisplayMode === 'timeline') {
      return variantGroups.flatMap((group) => group.variants.map((message) => ({ group, message })));
    }

    const selectedVariants = variantSelectionState.chatUuid === chatUuid ? variantSelectionState.selections : {};

    return variantGroups.map((group) => {
      const selection = group.rootId === pendingRegenerationRootId ? undefined : selectedVariants[group.rootId];
      const selectedMessage =
        selection?.variantCount === group.variants.length
          ? group.variants.find((variant) => variant.id === selection.messageId)
          : undefined;

      return { group, message: selectedMessage ?? group.variants.at(-1)! };
    });
  }, [chatUuid, pendingRegenerationRootId, variantDisplayMode, variantGroups, variantSelectionState]);
  const displayedMessages = useMemo(
    () => displayedVariantGroups.map(({ message }) => message),
    [displayedVariantGroups],
  );

  const showThinkingMessage = getShowThinkingMessage(status);
  const lastMessageId = displayedMessages.at(-1)?.id;
  const priorSourcesByMessageId = useMemo(() => buildPriorSourcesByMessageId(displayedMessages), [displayedMessages]);
  const renderableDurablePreviews = useMemo(
    () =>
      durablePreviews.flatMap((preview) => {
        const message = getRenderableDurablePreviewMessage(preview.message);
        return message ? [{ ...preview, message }] : [];
      }),
    [durablePreviews],
  );
  const activeStreamIsAlreadyPersisted = isDurableActiveStreamAlreadyPersisted({
    activeStreamId: overlayContext?.chatActiveStreamId,
    messages,
  });
  const effectiveChatUuid = chatUuid ?? overlayContext?.chatUuid;
  const activeStreamBaseIsHydrated = effectiveChatUuid
    ? isDurableActiveStreamBaseHydrated({
        activeStreamId: overlayContext?.chatActiveStreamId,
        chatUuid: effectiveChatUuid,
        messages,
      })
    : false;
  const isHydratingInterruptedStream =
    Boolean(overlayContext?.chatHasActiveStream) &&
    !activeStreamIsAlreadyPersisted &&
    !activeStreamBaseIsHydrated &&
    status === 'ready';
  const shouldShowLoadingOverlay = Boolean(isLoading || isHydratingInterruptedStream);
  const shouldShowGreeting = displayedMessages.length === 0 && !shouldShowLoadingOverlay;
  const hasVisiblePreviewText = renderableDurablePreviews.some(({ message }) => hasVisibleDurablePreviewText(message));
  const hasRenderableDurablePreview = renderableDurablePreviews.length > 0;

  useEffect(() => {
    const activeChatUuid = overlayContext?.chatUuid ?? chatUuid;
    if (!activeChatUuid) {
      return;
    }

    observeNovaTurnText({
      chatUuid: activeChatUuid,
      durablePreviewMessages: renderableDurablePreviews.map(({ message }) => message),
      messages,
    });

    if (status === 'ready') {
      settleNovaTurnTelemetry(activeChatUuid, 'completed_without_text');
    }
  }, [chatUuid, messages, overlayContext?.chatUuid, renderableDurablePreviews, status]);

  // With the chain of thought shown, its header provides the loading indication.
  const shouldShowThinkingMessage = showThinkingMessage && !hasVisiblePreviewText && !showChainOfThought;

  const ignorePreviewReaction = useCallback(() => {}, []);

  useEffect(() => {
    if (
      !messageNavigationTarget ||
      !effectiveChatUuid ||
      normalizeChatUuid(messageNavigationTarget.chatUuid) !== normalizeChatUuid(effectiveChatUuid) ||
      shouldShowLoadingOverlay
    ) {
      return;
    }

    const targetElement = messageElementsRef.current.get(messageNavigationTarget.messageUuid);
    if (!targetElement) return;

    scrollToElement(targetElement);
    clearMessageNavigationTarget?.(messageNavigationTarget);
  }, [
    clearMessageNavigationTarget,
    effectiveChatUuid,
    messageNavigationTarget,
    scrollToElement,
    displayedMessages,
    shouldShowLoadingOverlay,
  ]);

  const handleReaction = useCallback(
    (messageUuid: string, reaction: MessageReaction | null) => {
      setReactions((prev) => ({ ...prev, [messageUuid]: reaction }));
      onReaction?.(reaction, messageUuid);
    },
    [onReaction],
  );

  return (
    <div className="relative flex size-full min-w-0 flex-col">
      <div
        ref={messagesContainerRef}
        data-testid="web-chat-messages-container"
        className={`relative flex size-full min-w-0 flex-col gap-6 overflow-y-auto px-4 pt-4 ${className || ''}`}
      >
        {shouldShowGreeting && (
          <div className="pointer-events-none absolute inset-0 z-0">
            <Greeting message={welcomeMessage} icon={welcomeIcon} />
          </div>
        )}

        {displayedVariantGroups.map(({ group, message }) => {
          const variantIndex = group.variants.findIndex((variant) => variant.id === message.id);
          const selectVariant = (nextIndex: number) =>
            setVariantSelectionState((current) => {
              const currentSelections = current.chatUuid === chatUuid ? current.selections : {};
              return {
                chatUuid,
                selections: {
                  ...currentSelections,
                  [group.rootId]: {
                    messageId: group.variants[nextIndex]!.id,
                    variantCount: group.variants.length,
                  },
                },
              };
            });

          return (
            <div
              key={variantDisplayMode === 'timeline' ? message.id : group.rootId}
              ref={(element) => {
                if (element) messageElementsRef.current.set(message.id, element);
                else messageElementsRef.current.delete(message.id);
              }}
              data-message-uuid={message.id}
            >
              <MessageListItem
                message={message}
                reaction={reactions[message.id]}
                MessageRenderer={MessageRenderer}
                isLast={message.id === lastMessageId}
                isStreaming={status === 'streaming'}
                suppressWorkingState={message.id === lastMessageId && hasRenderableDurablePreview}
                onReaction={handleReaction}
                sourceFallbackMap={priorSourcesByMessageId.get(message.id)}
                toolRenderers={toolRenderers}
                dataPartRenderers={dataPartRenderers}
                enableSourcesRendering={enableSourcesRendering}
                showChainOfThought={showChainOfThought}
                variantIndex={variantIndex}
                variantCount={group.variants.length}
                onPreviousVariant={
                  variantDisplayMode === 'collapsed' && variantIndex > 0
                    ? () => selectVariant(variantIndex - 1)
                    : undefined
                }
                onNextVariant={
                  variantDisplayMode === 'collapsed' && variantIndex < group.variants.length - 1
                    ? () => selectVariant(variantIndex + 1)
                    : undefined
                }
              />
            </div>
          );
        })}

        {renderableDurablePreviews.map(({ key, message }) => (
          <MessageRenderer
            key={key}
            message={message}
            isLast
            isStreaming
            onReaction={ignorePreviewReaction}
            toolRenderers={toolRenderers}
            dataPartRenderers={dataPartRenderers}
            enableSourcesRendering={enableSourcesRendering}
            showChainOfThought={showChainOfThought}
          />
        ))}

        <ThinkingMessage show={shouldShowThinkingMessage} />

        <InlineError />

        {shouldShowLoadingOverlay && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="bg-background/90 border-border/20 rounded-lg border p-4 shadow-lg backdrop-blur-sm">
              <div data-testid="web-chat-loading-messages" className="flex items-center gap-3">
                <Loader2 className="size-4 animate-spin" />
                <span className="text-muted-foreground text-sm">Loading messages...</span>
              </div>
            </div>
          </div>
        )}

        <motion.div
          ref={messagesEndRef}
          className="min-h-[24px] min-w-[24px] shrink-0"
          onViewportLeave={onViewportLeave}
          onViewportEnter={onViewportEnter}
        />
      </div>

      <button
        aria-label="Scroll to bottom"
        data-testid="web-chat-scroll-to-bottom-button"
        className={`bg-background hover:bg-muted absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full border p-2 shadow-lg transition-all ${
          isAtBottom ? 'pointer-events-none scale-0 opacity-0' : 'pointer-events-auto scale-100 opacity-100'
        }`}
        onClick={() => scrollToBottom('smooth')}
        type="button"
      >
        <ArrowDownIcon data-testid="web-chat-scroll-to-bottom-icon" className="size-4" />
      </button>
    </div>
  );
}

function getShowThinkingMessage(status: string) {
  return status === 'submitted' || status === 'streaming';
}

interface MessageListItemProps {
  message: UIMessageWithMetadata;
  reaction: MessageReaction | null | undefined;
  MessageRenderer: ComponentType<PreviewMessageProps>;
  isLast: boolean;
  isStreaming: boolean;
  onReaction: (messageUuid: string, reaction: MessageReaction | null) => void;
  sourceFallbackMap?: SourcesMap;
  toolRenderers?: Record<string, ToolOutputRenderer>;
  dataPartRenderers?: Record<string, DataPartRenderer>;
  enableSourcesRendering?: boolean;
  showChainOfThought: boolean;
  suppressWorkingState?: boolean;
  variantIndex: number;
  variantCount: number;
  onPreviousVariant?: () => void;
  onNextVariant?: () => void;
}

const MessageListItem = memo(function MessageListItem({
  message,
  reaction,
  MessageRenderer,
  isLast,
  isStreaming,
  onReaction,
  sourceFallbackMap,
  toolRenderers,
  dataPartRenderers,
  enableSourcesRendering,
  showChainOfThought,
  suppressWorkingState,
  variantIndex,
  variantCount,
  onPreviousVariant,
  onNextVariant,
}: MessageListItemProps) {
  const renderedMessage = useMemo(
    () => ({
      ...message,
      metadata: {
        ...message.metadata,
        reaction: reaction !== undefined ? reaction : message.metadata?.reaction,
        files: message.metadata?.files ?? [],
      },
    }),
    [message, reaction],
  );
  const handleReaction = useCallback(
    (nextReaction: MessageReaction | null) => onReaction(message.id, nextReaction),
    [message.id, onReaction],
  );

  return (
    <MessageRenderer
      message={renderedMessage}
      isLast={isLast}
      isStreaming={isStreaming}
      onReaction={handleReaction}
      sourceFallbackMap={sourceFallbackMap}
      toolRenderers={toolRenderers}
      dataPartRenderers={dataPartRenderers}
      enableSourcesRendering={enableSourcesRendering}
      showChainOfThought={showChainOfThought}
      suppressWorkingState={suppressWorkingState}
      variantIndex={variantIndex}
      variantCount={variantCount}
      onPreviousVariant={onPreviousVariant}
      onNextVariant={onNextVariant}
    />
  );
});

interface MessageVariantGroup {
  rootId: string;
  variants: UIMessageWithMetadata[];
}

function insertPendingRegenerationMessage(
  messages: UIMessageWithMetadata[],
  pendingMessage?: UIMessageWithMetadata,
): UIMessageWithMetadata[] {
  if (!pendingMessage || messages.some((message) => message.id === pendingMessage.id)) return messages;

  const pendingRootId = getMessageVariantRootId(pendingMessage);
  const latestRootVariantIndex = messages.reduce(
    (latestIndex, message, index) =>
      message.role === 'assistant' && getMessageVariantRootId(message) === pendingRootId ? index : latestIndex,
    -1,
  );

  if (latestRootVariantIndex >= 0) {
    const insertionIndex = latestRootVariantIndex + 1;
    return [...messages.slice(0, insertionIndex), pendingMessage, ...messages.slice(insertionIndex)];
  }

  // AI SDK temporarily removes the target answer before the replacement stream materializes.
  // Keep the pending answer anchored to its user turn instead of moving it into an earlier turn.
  const latestUserIndex = messages.reduce(
    (latestIndex, message, index) => (message.role === 'user' ? index : latestIndex),
    -1,
  );

  if (latestUserIndex < 0) return [...messages, pendingMessage];

  const insertionIndex = latestUserIndex + 1;
  return [...messages.slice(0, insertionIndex), pendingMessage, ...messages.slice(insertionIndex)];
}

function groupMessageVariants(messages: UIMessageWithMetadata[]): MessageVariantGroup[] {
  const groups: MessageVariantGroup[] = [];
  const groupIndexes = new Map<string, number>();

  for (const message of messages) {
    if (message.role !== 'assistant') {
      groups.push({ rootId: message.id, variants: [message] });
      continue;
    }

    const rootId = getMessageVariantRootId(message);
    const existingIndex = groupIndexes.get(rootId);
    if (existingIndex === undefined) {
      groupIndexes.set(rootId, groups.length);
      groups.push({ rootId, variants: [message] });
    } else {
      groups[existingIndex]!.variants.push(message);
    }
  }

  return groups.map((group) => ({
    ...group,
    variants: [...group.variants].sort(
      (left, right) => getPersistedVariantIndex(left) - getPersistedVariantIndex(right),
    ),
  }));
}

function getMessageVariantRootId(message: UIMessageWithMetadata): string {
  return message.metadata?.regeneration?.rootMessageUuid ?? message.id;
}

function getPersistedVariantIndex(message: UIMessageWithMetadata): number {
  const variantIndex = message.metadata?.regeneration?.variantIndex;
  return typeof variantIndex === 'number' ? variantIndex : 1;
}
